import AppError from 'utils/appError';
import { ControllerExceptionCodes } from 'utils/controllerException';

const NETWORK_CODE_SET = new Set([
  // your app / custom
  'post_request_failed',
  'no_network_connectivity',

  // axios v1
  'err_network',

  // node-style codes (sometimes bubble up depending on environment)
  'econnreset',
  'econnrefused',
  'enotfound',
  'eai_again',
  'enetdown',
  'enetunreach',
  'ehostunreach',

  // timeouts (node often uses ETIMEDOUT; axios XHR uses ECONNABORTED for timeouts)
  'etimedout',
  'econnaborted',
]);

const NETWORK_TEXT_NEEDLES = [
  // fetch / browser / RN-ish messages
  'failed to fetch',
  'network request failed',
  'networkerror when attempting to fetch resource',
  'load failed',

  // axios common
  'network error',
  'err_network',
  'timeout',
  'etimedout',
  'econnreset',
  'econnrefused',
  'enotfound',
];

const ABORT_CODE_SET = new Set([
  // Axios v1
  'err_canceled',

  // Sometimes surfaced by libs / older axios / custom wrappers
  'cancelled',
  'canceled',
]);

const ABORT_TEXT_NEEDLES = [
  // Browser AbortController
  'aborterror',
  'the operation was aborted',

  // Generic
  'request aborted',
  'aborted',
  'canceled',
  'cancelled',
];

const normalize = (s?: unknown) => (typeof s === 'string' ? s.toLowerCase() : '');

const containsAny = (haystack: string, needles: string[]) => needles.some((n) => haystack.includes(n));

export const isNetworkError = (error: AppError | undefined): boolean => {
  if (!error) return false;

  const code = normalize(error.code);
  if (code && NETWORK_CODE_SET.has(code)) return true;

  const message = normalize(error.message);
  const debug = normalize(error.debug);

  // Check combined text once (avoids repeating `.includes` work)
  const combined = `${message} ${debug}`.trim();

  return combined.length > 0 && containsAny(combined, NETWORK_TEXT_NEEDLES);
};

export const isLoginError = (error: AppError | undefined): boolean => {
  if (!error) return false;

  const filterMsg: string[] = ['login_error'];

  const isLoginError = filterMsg.some((msg) => {
    if (error.code === msg) return true;

    return false;
  });

  return isLoginError;
};

// The back-end login flow fails token validation with "Tenant restore in progress" while a tenant restore
// is running (analogous to the "Tenant is suspended" login error). The message arrives in the 401 body
// (ProblemDetails detail), which AppError.fromApiError stores in message/debug.
export const isTenantRestoreInProgressError = (error: AppError | undefined): boolean => {
  if (!error) return false;

  const combined = `${normalize(error.message)} ${normalize(error.debug)}`.trim();

  return combined.includes('tenant restore in progress');
};

// The back-end rejects every call from a suspended tenant with "Tenant is suspended" in the 401 body
// (same channel as the restore-in-progress state above).
export const isTenantSuspendedError = (error: AppError | undefined): boolean => {
  if (!error) return false;

  const combined = `${normalize(error.message)} ${normalize(error.debug)}`.trim();

  return combined.includes('tenant is suspended');
};

// A failed overwrite restore leaves the tenant suspended on purpose (part of its data may be missing); the back-end
// reports "Tenant restore failed" in the 401 body until a restore succeeds and releases the lock.
export const isTenantRestoreFailedError = (error: AppError | undefined): boolean => {
  if (!error) return false;

  const combined = `${normalize(error.message)} ${normalize(error.debug)}`.trim();

  return combined.includes('tenant restore failed');
};

// A refusal of the identity layer (a personal Google account, a token of another directory) is a 401 whose
// ProblemDetails body carries an IDP code: as its own "code" property, which fromApiError keeps in data, and
// as a prefix of the detail text, which lands in message/debug. The code is read first, the text second.
const identityProviderCodePattern = /IDP\d{4}/;

export const getIdentityProviderErrorCode = (error: AppError | undefined): ControllerExceptionCodes | undefined => {
  if (!error) return undefined;

  const codeFromBody = error.data?.code;
  if (codeFromBody && identityProviderCodePattern.test(codeFromBody)) {
    return codeFromBody as ControllerExceptionCodes;
  }

  const match = identityProviderCodePattern.exec(`${error.message ?? ''} ${error.debug ?? ''}`);

  return match ? (match[0] as ControllerExceptionCodes) : undefined;
};

export const isAbortOrCancelError = (error: AppError | undefined): boolean => {
  if (!error) return false;

  const code = normalize(error.code);
  if (code && ABORT_CODE_SET.has(code)) return true;

  // Combine once to minimize work
  const combined = `${normalize(error.message)} ${normalize(error.debug)}`.trim();

  return combined.length > 0 && containsAny(combined, ABORT_TEXT_NEEDLES);
};

export const isAuthReloadError = (error: AppError | undefined): boolean => {
  return (
    (error?.code === '403' &&
      (error?.debug?.startsWith('Path: /auth') ||
        error?.debug?.startsWith('Path: /tenant') ||
        error?.debug?.startsWith('Path: tenant') ||
        error?.debug?.startsWith('Path: iso'))) ??
    false
  );
};
